import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "../context/TranslationContext";
import {
  fetchClientMatterDetails,
  lookupClientDocument,
  lookupClientCpf,
} from "../helpers/apiHelper";
import { useNavigate } from "react-router-dom";
import {
  CLIENT_DOCUMENT_TYPES,
  formatClientDocument,
  isValidClientDocument,
  isValidCpf,
  normalizeClientDocument,
} from "../helpers/validationHelper";
import "../styles/CpfSearch.css";
import Breadcrumb from "./Breadcrumb";
import Alerts from "./Alerts";
import { useAuth } from "../context/AuthContext";
import { FaCog } from "react-icons/fa";
import { formatDateToYYYYMMDD } from "../helpers/utilityHelper";
import "../styles/Views.css";
import { useFeatureFlags } from "../context/FeatureFlagsContext";
import SearchModeToggle from "./SearchModeToggle";
import { CLIENT_ADVANCED_SEARCH_PATH } from "../helpers/advancedSearchHelper";
import "../styles/AdvancedSearch.css";

const CpfSearch = () => {
  const navigate = useNavigate();
  const { t, language } = useTranslation();
  const { features } = useFeatureFlags();
  const clientSearchEnabled = features.clientSearch;
  const advancedSearchEnabled = features.advancedClientSearch === true;
  const [documentType, setDocumentType] = useState(CLIENT_DOCUMENT_TYPES.CPF);
  const [documentValue, setDocumentValue] = useState("");
  const [isDocumentValid, setIsDocumentValid] = useState(true);
  const [resultDocumentType, setResultDocumentType] = useState(
    CLIENT_DOCUMENT_TYPES.CPF,
  );
  const [resultDocumentValue, setResultDocumentValue] = useState("");
  const [result, setResult] = useState(null);
  const [resultMatter, setResultMatter] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { tokens } = useAuth();
  const [foundPartyId, setFoundPartyId] = useState("");
  const [loadingClientDetails, setLoadingClientDetails] = useState(false);
  const activeRequestRef = useRef(null);

  useEffect(
    () => () => {
      activeRequestRef.current?.abort();
    },
    [],
  );

  const clearResults = () => {
    setResult(null);
    setResultMatter(null);
    setError(null);
    setResultDocumentValue("");
  };

  const handleDocumentChange = (event) => {
    if (activeRequestRef.current) {
      activeRequestRef.current.abort();
      activeRequestRef.current = null;
      setLoading(false);
      setLoadingClientDetails(false);
      clearResults();
    }
    const nextValue = clientSearchEnabled
      ? formatClientDocument(event.target.value, documentType)
      : event.target.value;
    setDocumentValue(nextValue);
    setIsDocumentValid(
      nextValue === ""
      || (
        clientSearchEnabled
          ? isValidClientDocument(nextValue, documentType)
          : isValidCpf(nextValue)
      ),
    );
  };

  const handleSearch = useCallback(async () => {
    const isValid = clientSearchEnabled
      ? isValidClientDocument(documentValue, documentType)
      : isValidCpf(documentValue);
    if (!isValid) {
      setIsDocumentValid(false);
      return;
    }

    setLoading(true);
    setLoadingClientDetails(false);
    clearResults();
    const token = tokens.idToken;
    activeRequestRef.current?.abort();
    const controller = new AbortController();
    activeRequestRef.current = controller;
    setResultDocumentType(documentType);
    setResultDocumentValue(normalizeClientDocument(documentValue));
    try {
      const lookupResponse = clientSearchEnabled
        ? await lookupClientDocument(
            documentType,
            documentValue,
            token,
            controller.signal,
          )
        : await lookupClientCpf(documentValue, token, controller.signal);
      if (controller.signal.aborted) return;
      setResult(lookupResponse);

      if (!lookupResponse?.clientDetails) {
        if (lookupResponse?.found === false && !lookupResponse?.message_en) {
          setError(
            clientSearchEnabled
              ? t("clientSearch.noResults")
              : t("cpfSearch.noResults"),
          );
        }
        return;
      }

      setLoadingClientDetails(true);
      const responseMatter = await fetchClientMatterDetails(
        token,
        lookupResponse.clientDetails.matterId,
        controller.signal,
      );
      if (controller.signal.aborted) return;
      setFoundPartyId(lookupResponse.clientDetails.partyId);
      setResultMatter(responseMatter);
      setDocumentValue("");
    } catch (requestError) {
      if (requestError?.name === "AbortError") return;
      setError(
        clientSearchEnabled
          ? t("clientSearch.searchError")
          : t("cpfSearch.searchError"),
      );
    } finally {
      if (activeRequestRef.current === controller) {
        activeRequestRef.current = null;
        setLoadingClientDetails(false);
        setLoading(false);
      }
    }
  }, [
    clientSearchEnabled,
    documentType,
    documentValue,
    t,
    tokens,
  ]);

  const handleBack = () => {
    navigate("/");
  };

  return (
    <div className="portal-page cpfsearch-container">
      <div className="portal-page-breadcrumb">
        <Breadcrumb
          items={[
            { label: t("breadcrumb.home"), link: "/" },
            {
              label: t(
                clientSearchEnabled
                  ? "breadcrumb.clientSearch"
                  : "breadcrumb.cpfSearch",
              ),
            },
          ]}
        />
      </div>
      <header className="portal-page-header global-search-page-header">
        <div>
          <p className="portal-page-kicker">
            {t(
              clientSearchEnabled
                ? "breadcrumb.clientSearch"
                : "breadcrumb.cpfSearch",
            )}
          </p>
          <h2 className="portal-page-title">
            {t(clientSearchEnabled ? "clientSearch.title" : "cpfSearch.title")}
          </h2>
        </div>
        {advancedSearchEnabled && (
          <SearchModeToggle
            mode="simple"
            onChange={() => navigate(CLIENT_ADVANCED_SEARCH_PATH)}
          />
        )}
      </header>

      {resultMatter?.alerts?.length > 0 && <Alerts alerts={resultMatter.alerts} clientName={resultMatter.details.name} />}

      <section className="portal-page-panel cpfsearch-panel">
        <form
          className="portal-inline-form"
          onSubmit={(event) => {
            event.preventDefault();
            handleSearch();
          }}
        >
          {clientSearchEnabled && (
            <div className="client-search-type-field">
              <label className="portal-label" htmlFor="clientDocumentType">
                {t("clientSearch.documentType")}
              </label>
              <select
                id="clientDocumentType"
                className="portal-input"
                value={documentType}
                onChange={(event) => {
                  activeRequestRef.current?.abort();
                  activeRequestRef.current = null;
                  setLoading(false);
                  setLoadingClientDetails(false);
                  setDocumentType(event.target.value);
                  setDocumentValue("");
                  setIsDocumentValid(true);
                  clearResults();
                }}
              >
                <option value={CLIENT_DOCUMENT_TYPES.CPF}>
                  {t("clientSearch.types.CPF")}
                </option>
                <option value={CLIENT_DOCUMENT_TYPES.CNPJ}>
                  {t("clientSearch.types.CNPJ")}
                </option>
              </select>
            </div>
          )}
          <label className="portal-label" htmlFor="cpfInput">
            {clientSearchEnabled
              ? t(`clientSearch.types.${documentType}`)
              : t("cpfSearch.cpf")}
          </label>
          <div className="portal-inline-form-controls cpfsearch-controls">
            <input
              type="text"
              id="cpfInput"
              inputMode="numeric"
              autoComplete="off"
              maxLength={documentType === CLIENT_DOCUMENT_TYPES.CNPJ ? 18 : 14}
              value={documentValue}
              onChange={handleDocumentChange}
              placeholder={
                clientSearchEnabled
                  ? t(`clientSearch.placeholders.${documentType}`)
                  : t("cpfSearch.placeholder")
              }
              className={`portal-input ${!isDocumentValid ? "invalid" : ""}`}
              aria-invalid={!isDocumentValid}
              aria-describedby={
                clientSearchEnabled && !isDocumentValid
                  ? "client-document-error"
                  : undefined
              }
            />

            <button
              type="submit"
              className="portal-button"
              disabled={
                loading
                || !isDocumentValid
                || (clientSearchEnabled && !documentValue)
              }
            >
              {loading ? t("cpfSearch.searching") : t("cpfSearch.button")}
            </button>
          </div>
          {clientSearchEnabled && !isDocumentValid && (
            <p id="client-document-error" className="portal-feedback error">
              {t(`clientSearch.invalid.${documentType}`)}
            </p>
          )}
        </form>

        {error && <p className="portal-feedback error">{error}</p>}

        {result && <p className="cpfsearch-result-copy">{language === "pt" ? result.message_pt : result.message_en}</p>}
      </section>

      {loadingClientDetails && (
        <div className="portal-page-panel">
          <p className="flex-row loading-message">
            <FaCog className="spinner-cog" /> {t("cpfSearch.loadingClientDetails")}{" "}
          </p>
        </div>
      )}

      {resultMatter && !error && (
        <section className="portal-page-panel client-details mt-20">
          <div className="field-row">
            <div className="field-label">{t("cpfSearch.name")}:</div>
            <div
              className="field-value link"
              onClick={() =>
                navigate(
                  resultMatter?.matterId
                    ? `/client-details-party/${foundPartyId}?matterId=${encodeURIComponent(resultMatter.matterId)}`
                    : `/client-details-party/${foundPartyId}`,
                  {
                  state: { matterId: resultMatter?.matterId, matterName: resultMatter?.details?.name },
                  },
                )
              }
            >
              {resultMatter.details.partyName}
            </div>
          </div>
          <div className="field-row">
            <div className="field-label">
              {clientSearchEnabled
                ? t(`clientSearch.types.${resultDocumentType}`)
                : t("cpfSearch.cpf")}
              :
            </div>
            <div className="field-value">
              {clientSearchEnabled
                ? formatClientDocument(
                    resultDocumentValue,
                    resultDocumentType,
                  )
                : resultMatter.details.cpf}
            </div>
          </div>
          <div className="field-row">
            <div className="field-label">{t("cpfSearch.matterName")}:</div>
            <div className="field-value">{resultMatter.details.name}</div>
          </div>
          <div className="field-row">
            <div className="field-label">{t("cpfSearch.retainerSignedDate")}:</div>
            <div className="field-value">{formatDateToYYYYMMDD(resultMatter.details.retainedSignedDate) || "—"}</div>
          </div>
          <div className="field-row">
            <div className="field-label">{t("cpfSearch.questionnaireCompletedDate")}:</div>
            <div className="field-value">{formatDateToYYYYMMDD(resultMatter.details.questionnaireCompletedAt) || "—"}</div>
          </div>
          <div className="field-row">
            <div className="field-label">{t("cpfSearch.questionnaireLink")}:</div>
            <div className="field-value">{resultMatter.details.questionnaireLink || t("clientsTable.emptyStates.noQuestionnaireLink")}</div>
          </div>
          <div className="field-row">
            <div className="field-label">{t("cpfSearch.email")}:</div>
            <div className="field-value">{resultMatter.details.email || t("clientsTable.emptyStates.noEmailAddress")}</div>
          </div>
          <div className="field-row">
            <div className="field-label">{t("cpfSearch.phone")}:</div>
            <div className="field-value">{resultMatter.details.phone || t("clientsTable.emptyStates.noPhoneNumber")}</div>
          </div>
        </section>
      )}

      <div onClick={handleBack} className="link mt-20 portal-page-footer-link">
        {t("back")}
      </div>
    </div>
  );
};

export default CpfSearch;
